import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { useLanguage } from "../i18n/LanguageContext";
import {
  checkAppUpdate,
  markManualTriggerHandled,
  openAppStore,
  snoozeAppUpdate,
} from "../services/appUpdateService";
import "../styles/app-update.css";

const FALLBACK_COPY = {
  tr: {
    title: "Yeni bir güncelleme hazır",
    message: "Aile Yönetimi'nin en güncel deneyimi hazır. Devam etmek için uygulamayı güncelleyin.",
    update: "Güncelle",
    later: "Sonra",
  },
  en: {
    title: "A new update is available",
    message: "The latest Aile Yönetimi experience is ready. Update the app to continue.",
    update: "Update",
    later: "Later",
  },
};

export default function AppUpdateGate() {
  const { lang } = useLanguage();
  const [update, setUpdate] = useState(null);
  const updateButtonRef = useRef(null);
  const languageRef = useRef(lang);

  useEffect(() => {
    languageRef.current = lang;
  }, [lang]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let disposed = false;
    let appStateHandle;
    const retryTimers = new Set();
    const retryDelays = [5000, 20000];
    const clearRetryTimers = () => {
      retryTimers.forEach((retryTimer) => window.clearTimeout(retryTimer));
      retryTimers.clear();
    };
    const runCheck = (retryAttempt = 0) => {
      // Yeni bir app activation önceki başarısız kontrol zincirinin bekleyen
      // timerlarını geçersiz kılar; her aktivasyonda tek retry zinciri çalışır.
      if (retryAttempt === 0) clearRetryTimers();
      checkAppUpdate(languageRef.current, { forceNetwork: true }).then((result) => {
        if (disposed) return;
        // Taze "güncelleme yok/kapalı" cevabı daha önce açık kalan popupı da kapatır.
        setUpdate(result?.update || null);
        if (result?.status === "failure" && retryAttempt < retryDelays.length) {
          const baseDelay = retryDelays[retryAttempt];
          const jitteredDelay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));
          const retryTimer = window.setTimeout(() => {
            retryTimers.delete(retryTimer);
            if (!disposed && !document.hidden) runCheck(retryAttempt + 1);
          }, jitteredDelay);
          retryTimers.add(retryTimer);
        }
      }).catch(() => {
        if (!disposed) setUpdate(null);
      });
    };

    // Gecikme yalnız görsel katman sırasını düzenler; kontrol doğruluğu cache ve
    // başarılı-istek zamanı üzerinden sağlanır.
    const timerId = window.setTimeout(() => {
      runCheck();
    }, 2500);
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) runCheck();
    }).then((handle) => {
      appStateHandle = handle;
      if (disposed) handle.remove();
    }).catch(() => {});

    return () => {
      disposed = true;
      window.clearTimeout(timerId);
      clearRetryTimers();
      appStateHandle?.remove();
    };
  }, []);

  useEffect(() => {
    const required = update?.required === true;
    window.__APP_FORCE_UPDATE_ACTIVE__ = required;
    if (!update) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => updateButtonRef.current?.focus(), 50);

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !required) setUpdate(null);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      window.__APP_FORCE_UPDATE_ACTIVE__ = false;
    };
  }, [update]);

  if (!update) return null;

  const fallback = FALLBACK_COPY[lang === "en" ? "en" : "tr"];
  const handleLater = () => {
    if (update.required) return;
    if (update.reason === "manual") markManualTriggerHandled(update.manualTriggerId);
    snoozeAppUpdate(update);
    setUpdate(null);
  };
  const handleUpdate = () => {
    if (update.reason === "manual") markManualTriggerHandled(update.manualTriggerId);
    openAppStore(update.storeUrl);
  };

  return (
    <div className="appUpdateOverlay" role="presentation">
      <section
        className="appUpdateCard"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="app-update-title"
        aria-describedby="app-update-message"
      >
        <div className="appUpdateIcon" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL || ""}/logo192.png`} alt="" />
          <span className="appUpdateBadge">↑</span>
        </div>

        <h2 id="app-update-title">{update.title || fallback.title}</h2>
        <p id="app-update-message">{update.message || fallback.message}</p>

        <div className="appUpdateActions">
          {!update.required && (
            <button type="button" className="appUpdateButton appUpdateButtonLater" onClick={handleLater}>
              {update.laterLabel || fallback.later}
            </button>
          )}
          <button
            ref={updateButtonRef}
            type="button"
            className="appUpdateButton appUpdateButtonPrimary"
            onClick={handleUpdate}
          >
            {update.updateLabel || fallback.update}
          </button>
        </div>
      </section>
    </div>
  );
}
